// File: src/auth/AuthContext.jsx
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { absorbHashToken, syncCookieTokenToLocalStorage, getAuthToken } from "../api/config";

function decodeJwt(token) {
  try {
    const base = token.split(".")[1];
    const json = JSON.parse(atob(base.replace(/-/g, "+").replace(/_/g, "/")));
    return json || {};
  } catch { return {}; }
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState("");

  // On boot: absorb #token, copy cookie, then read token
  useEffect(() => {
    absorbHashToken();
    syncCookieTokenToLocalStorage();
    setToken(getAuthToken());
  }, []);

  // Auto-logout on expiry (if JWT has exp)
  useEffect(() => {
    if (!token) return;
    const { exp } = decodeJwt(token);
    if (!exp) return; // no exp present
    const ms = exp * 1000 - Date.now();
    if (ms <= 0) { doLogout(); return; }
    const t = setTimeout(doLogout, ms + 500);
    return () => clearTimeout(t);
  }, [token]);

  function doLogout() {
    try { localStorage.removeItem("token"); } catch {}
    try { localStorage.removeItem("authToken"); } catch {}
    // Remove cookies at current domain and parent .buildstart.au (if applicable)
    const expire = "Thu, 01 Jan 1970 00:00:01 GMT";
    document.cookie = `token=; Path=/; Expires=${expire}`;
    const host = window.location.hostname;
    const parts = host.split(".");
    if (parts.length >= 2) {
      const parent = `.${parts.slice(-2).join(".")}`; // e.g., .buildstart.au
      document.cookie = `token=; Path=/; Domain=${parent}; Expires=${expire}; Secure; SameSite=Lax`;
    }
    setToken("");
    // Optional: redirect to login
    // window.location.href = "/login";
  }

  const value = useMemo(() => ({ token, setToken, logout: doLogout }), [token]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}